import React from "react";

export type Tone = "success" | "warning" | "error" | "neutral" | "info" | "brand";

const TONE_CLS: Record<Tone, string> = {
  success: "bg-success-100 text-success-600",
  warning: "bg-warning-100 text-warning-600",
  error: "bg-danger-100 text-danger-600",
  neutral: "bg-ink-100 text-ink-500",
  info: "bg-info-100 text-info-600",
  brand: "bg-brand-100 text-brand-600",
};

export interface BadgeProps {
  tone?: Tone;
  children: React.ReactNode;
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({ tone = "neutral", children, className = "" }) => (
  <span
    className={[
      "inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide rounded-md px-2 py-0.5 font-dmsans",
      TONE_CLS[tone],
      className,
    ].join(" ")}
  >
    {children}
  </span>
);

// ─── Domain helpers ─────────────────────────────────────────────────────────

const FORM_STATUS_TONE: Record<string, Tone> = {
  active: "success",
  draft: "neutral",
  inactive: "neutral",
  archived: "neutral",
};

export const FormStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <Badge tone={FORM_STATUS_TONE[status] ?? "neutral"}>{status}</Badge>
);

const SUBMISSION_STATUS: Record<string, { label: string; tone: Tone }> = {
  new: { label: "New", tone: "brand" },
  read: { label: "Read", tone: "neutral" },
  pending: { label: "Pending", tone: "warning" },
  approved: { label: "Approved", tone: "success" },
  rejected: { label: "Rejected", tone: "error" },
  starred: { label: "Starred", tone: "warning" },
  archived: { label: "Archived", tone: "neutral" },
  spam: { label: "Spam", tone: "error" },
};

export const SubmissionStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = SUBMISSION_STATUS[status] ?? { label: status, tone: "neutral" as Tone };
  return <Badge tone={cfg.tone}>{cfg.label}</Badge>;
};

export default Badge;
